import { Heading, HStack, useColorMode, VStack } from '@chakra-ui/react'
import { Body, Button, QRCode, Spinner } from '@missionlabs/react'
import {
    CloseCircleIcon,
    HelpIcon,
    TickCircleIcon,
    WarningIcon
} from '@missionlabs/react/circleloop'
import { ProvisioningFlow, ProvisioningFlowTone } from '@missionlabs/types'
import { FC, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ProvisioningFlowLines } from '../DeviceProvisioningFlow'

export const ESIM_CUSTOMER_STATUSES = [
    'provisioning',
    'ready_to_install',
    'installed',
    'disabled',
    'removed',
    'failed'
] as const

export type ESimCustomerStatus = (typeof ESIM_CUSTOMER_STATUSES)[number]

const TONE_BY_STATUS: Record<ESimCustomerStatus, ProvisioningFlowTone> = {
    provisioning: 'progress',
    ready_to_install: 'action',
    installed: 'success',
    disabled: 'warning',
    removed: 'action',
    failed: 'error'
}

const PRIMARY_QR_STATUSES: ESimCustomerStatus[] = ['ready_to_install', 'removed']

export function isESimCustomerStatus(value?: string): value is ESimCustomerStatus {
    return !!value && (ESIM_CUSTOMER_STATUSES as readonly string[]).includes(value)
}

export function firstProvisioningFlow(
    provisioningFlow?: ProvisioningFlow[] | ProvisioningFlow
): ProvisioningFlow | undefined {
    if (!provisioningFlow) return undefined
    return Array.isArray(provisioningFlow) ? provisioningFlow[0] : provisioningFlow
}

const StatusIcon: FC<{ tone: ProvisioningFlowTone }> = ({ tone }) => {
    const { colorMode } = useColorMode()

    const icons: Record<ProvisioningFlowTone, ReactNode> = {
        progress: <Spinner size="sm" data-testid="esim-status-progress" />,
        action: (
            <HelpIcon
                boxSize="24px"
                color={`${colorMode}.coolGrey.80`}
                data-testid="esim-status-action"
            />
        ),
        success: (
            <TickCircleIcon boxSize="24px" color="#3EBC9E" data-testid="esim-status-success" />
        ),
        warning: <WarningIcon boxSize="24px" data-testid="esim-status-warning" />,
        error: <CloseCircleIcon boxSize="24px" color="#DA0075" data-testid="esim-status-error" />
    }

    return <>{icons[tone]}</>
}

export interface ESimCustomerStatusViewProps {
    customerStatus: ESimCustomerStatus
    flow?: ProvisioningFlow
    lpa?: string
}

export const ESimCustomerStatusView: FC<ESimCustomerStatusViewProps> = ({
    customerStatus,
    flow,
    lpa
}) => {
    const { t } = useTranslation()
    const [showSecondaryQr, setShowSecondaryQr] = useState(false)

    const tone = flow?.tone ?? TONE_BY_STATUS[customerStatus]
    const showPrimaryQr = !!lpa && PRIMARY_QR_STATUSES.includes(customerStatus)
    const showInstallOnAnotherPhone = customerStatus === 'installed' && !!lpa

    return (
        <VStack spacing="16px" align="stretch" data-testid="esim-customer-status">
            <HStack spacing="12px" align="center">
                <StatusIcon tone={tone} />
                {flow?.name && <Heading size="h2">{flow.name}</Heading>}
            </HStack>
            <ProvisioningFlowLines lines={flow?.lines} />
            {showPrimaryQr && lpa && (
                <Body data-testid="esim-qr-code">
                    <QRCode url={lpa} />
                </Body>
            )}
            {showInstallOnAnotherPhone && lpa && (
                <VStack spacing="16px" align="stretch">
                    <Button
                        type="button"
                        size="md"
                        variant="link"
                        alignSelf="flex-start"
                        data-testid="esim-install-on-another-phone"
                        onClick={() => setShowSecondaryQr(open => !open)}
                    >
                        {t('devices.add.eSim.activation.install_on_another_phone', {
                            defaultValue: 'Install on another phone'
                        })}
                    </Button>
                    {showSecondaryQr && (
                        <Body data-testid="esim-qr-code">
                            <QRCode url={lpa} />
                        </Body>
                    )}
                </VStack>
            )}
        </VStack>
    )
}
